import { isPendingInvitationClient } from "../../invitations/invitedClientUiGuards";
import {
  INVITATION_STATE,
  normalizeInvitationState,
} from "../../invitations/invitationIdentity";

const toNonNegativeInteger = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

const toTimestampMs = (value) => {
  if (value && typeof value.toMillis === "function") {
    const ms = value.toMillis();
    return Number.isFinite(ms) ? ms : 0;
  }

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return 0;
};

const toNormalizedId = (value) => String(value ?? "").trim().toLowerCase();
const toTrimmedString = (value) => String(value ?? "").trim();

const toOptionalOrder = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildAcceptedInvitationIdSet = (invitationSummaries = []) => {
  if (!Array.isArray(invitationSummaries) || invitationSummaries.length === 0) {
    return new Set();
  }

  const acceptedInvitationIds = new Set();
  invitationSummaries.forEach((summary) => {
    const invitationId = toNormalizedId(summary?.invitationId);
    if (!invitationId) return;
    if (normalizeInvitationState(summary?.state) !== INVITATION_STATE.ACCEPTED) {
      return;
    }
    acceptedInvitationIds.add(invitationId);
  });

  return acceptedInvitationIds;
};

const buildMatchedPendingUserIdSet = (clients = [], acceptedInvitationIds = new Set()) => {
  if (!Array.isArray(clients) || clients.length === 0) {
    return new Set();
  }

  const matchedPendingUserIds = new Set();
  clients.forEach((client) => {
    if (!isPendingInvitationClient(client)) return;
    const invitationId = toNormalizedId(client?.invitationId);
    if (invitationId && acceptedInvitationIds.has(invitationId)) return;
    const pendingUserId = toNormalizedId(client?.pendingUserId);
    if (!pendingUserId) return;
    matchedPendingUserIds.add(pendingUserId);
  });

  return matchedPendingUserIds;
};

export const selectPendingUserBlockEntries = ({
  pendingWorkspaceSummaries = [],
  clients = [],
  invitationSummaries = [],
} = {}) => {
  if (
    !Array.isArray(pendingWorkspaceSummaries) ||
    pendingWorkspaceSummaries.length === 0
  ) {
    return [];
  }

  const acceptedInvitationIds = buildAcceptedInvitationIdSet(invitationSummaries);
  const matchedPendingUserIds = buildMatchedPendingUserIdSet(
    clients,
    acceptedInvitationIds,
  );

  return pendingWorkspaceSummaries
    .map((summary, index) => {
      const pendingUserId = String(summary?.pendingUserId ?? "").trim();
      const normalizedPendingUserId = toNormalizedId(pendingUserId);
      const blocksCount = toNonNegativeInteger(summary?.blocksCount);
      const blocks = Array.isArray(summary?.blocks) ? summary.blocks : [];

      return {
        id: pendingUserId,
        pendingUserId,
        normalizedPendingUserId,
        invitationId: String(summary?.invitationId ?? "").trim(),
        invitationEmail: String(summary?.invitationEmail ?? "").trim(),
        blocksCount,
        blocks,
        timestampMs: toTimestampMs(summary?.lastUpdateMs),
        fallbackIndex: Number.isFinite(Number(summary?.fallbackIndex))
          ? Number(summary.fallbackIndex)
          : index,
      };
    })
    .filter((entry) => {
      if (!entry.pendingUserId) return false;
      if (entry.blocksCount <= 0) return false;
      if (acceptedInvitationIds.has(toNormalizedId(entry?.invitationId))) {
        return true;
      }
      return !matchedPendingUserIds.has(entry.normalizedPendingUserId);
    })
    .sort((left, right) => {
      if (left.timestampMs !== right.timestampMs) {
        return right.timestampMs - left.timestampMs;
      }
      return left.fallbackIndex - right.fallbackIndex;
    });
};

export const getPendingUserBlocksTotalCount = (entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) return 0;
  return entries.reduce(
    (total, entry) => total + toNonNegativeInteger(entry?.blocksCount),
    0,
  );
};

export const flattenPendingUserBlocksFromEntries = (entries = []) => {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }

  const flattened = [];

  entries.forEach((entry) => {
    const pendingUserId = toTrimmedString(entry?.pendingUserId);
    if (!pendingUserId) return;

    const blocks = Array.isArray(entry?.blocks) ? entry.blocks : [];
    const sortedBlocks = blocks
      .map((block, index) => {
        if (!block || typeof block !== "object") return null;
        const blockId = toTrimmedString(block?.id);
        if (!blockId) return null;
        if (!Array.isArray(block?.workouts)) return null;

        return {
          blockData: block,
          blockId,
          blockOrder: toOptionalOrder(block?.order),
          fallbackIndex: index,
        };
      })
      .filter(Boolean)
      .sort((left, right) => {
        if (left.blockOrder == null && right.blockOrder != null) return 1;
        if (left.blockOrder != null && right.blockOrder == null) return -1;
        if (left.blockOrder !== right.blockOrder) {
          return (left.blockOrder ?? 0) - (right.blockOrder ?? 0);
        }
        return left.fallbackIndex - right.fallbackIndex;
      });

    sortedBlocks.forEach((blockEntry) => {
      flattened.push({
        id: `${pendingUserId}:${blockEntry.blockId}`,
        pendingUserId,
        invitationId: toTrimmedString(entry?.invitationId),
        invitationEmail: toTrimmedString(entry?.invitationEmail),
        timestampMs: Number(entry?.timestampMs) || 0,
        workspaceFallbackIndex: Number(entry?.fallbackIndex) || 0,
        blockId: blockEntry.blockId,
        blockData: blockEntry.blockData,
      });
    });
  });

  return flattened;
};

export const shouldShowPendingUserBlocksSection = (entries = []) =>
  Array.isArray(entries) && entries.length > 0;

export const removePendingBlockFromWorkspaceSummaries = ({
  pendingWorkspaceSummaries = [],
  pendingUserId,
  blockId,
} = {}) => {
  if (!Array.isArray(pendingWorkspaceSummaries) || pendingWorkspaceSummaries.length === 0) {
    return [];
  }

  const normalizedPendingUserId = toNormalizedId(pendingUserId);
  const normalizedBlockId = toNormalizedId(blockId);
  if (!normalizedPendingUserId || !normalizedBlockId) {
    return pendingWorkspaceSummaries;
  }

  return pendingWorkspaceSummaries
    .map((summary) => {
      const summaryPendingUserId = toNormalizedId(summary?.pendingUserId);
      if (summaryPendingUserId !== normalizedPendingUserId) {
        return summary;
      }

      const blocks = Array.isArray(summary?.blocks) ? summary.blocks : [];
      const nextBlocks = blocks.filter((block) => {
        const summaryBlockId = toNormalizedId(block?.id);
        return summaryBlockId !== normalizedBlockId;
      });

      if (nextBlocks.length === blocks.length) {
        return summary;
      }

      return {
        ...summary,
        blocks: nextBlocks,
        blocksCount: nextBlocks.length,
      };
    })
    .filter((summary) => {
      const blocksCount = toNonNegativeInteger(summary?.blocksCount);
      const nutritionPlansCount = toNonNegativeInteger(summary?.nutritionPlansCount);
      return blocksCount > 0 || nutritionPlansCount > 0;
    });
};
